export type CashDenominationKey =
  | "hundred"
  | "fifty"
  | "twenty"
  | "ten"
  | "five"
  | "two"
  | "one"
  | "quarter"
  | "dime"
  | "nickel"
  | "penny";

export const DENOMINATION_VALUES: Record<CashDenominationKey, number> = {
  hundred: 100,
  fifty: 50,
  twenty: 20,
  ten: 10,
  five: 5,
  two: 2,
  one: 1,
  quarter: 0.25,
  dime: 0.1,
  nickel: 0.05,
  penny: 0.01,
};

export const DENOMINATION_LABELS: Record<CashDenominationKey, string> = {
  hundred: "$100",
  fifty: "$50",
  twenty: "$20",
  ten: "$10",
  five: "$5",
  two: "$2",
  one: "$1",
  quarter: "25¢",
  dime: "10¢",
  nickel: "5¢",
  penny: "1¢",
};

export const DENOMINATION_ORDER: CashDenominationKey[] = [
  "hundred",
  "fifty",
  "twenty",
  "ten",
  "five",
  "two",
  "one",
  "quarter",
  "dime",
  "nickel",
  "penny",
];

export interface DenominationEntry {
  denomination: CashDenominationKey;
  count: number;
}

export type CashTransactionType =
  | "sale"
  | "refund"
  | "cash_in"
  | "cash_out"
  | "opening_balance"
  | "closeout"
  | "other";

export type PayoutCategory = "expense" | "deposit";

export interface CashTransaction {
  id: string;
  type: CashTransactionType;
  direction: 1 | -1;
  amount: number;
  tip_amount?: number | null;
  description?: string | null;
  custom_type_label?: string | null;
  payout_category?: PayoutCategory | null;
  linked_payment_id?: number | null;
  linked_transaction_id?: number | null;
  staff_id?: string | null;
  voided_at?: string | null;
  voided_by_staff_id?: string | null;
  created_dt?: string | null;
  running_balance?: number;
}

export type CashDrawerSessionStatus = "open" | "closed";

export interface CashDrawerMetrics {
  cash_sales: number;
  /** Sum of check_sale transactions for the session — the system-side
   *  "expected checks" to compare against counted_checks on close. */
  check_sales: number;
  refunds: number;
  paid_in: number;
  expenses: number;
  deposits: number;
  tips: number;
}

export interface CashDrawerSession {
  id: string;
  salon_id: number;
  opening_balance: number;
  expected_balance: number;
  counted_balance: number | null;
  counted_denominations: DenominationEntry[] | null;
  /** Physical total of checks counted at closeout. Null until set. */
  counted_checks: number | null;
  variance: number | null;
  status: CashDrawerSessionStatus;
  auto_opened: boolean;
  closing_note: string | null;
  /** YYYY-MM-DD in the salon's timezone; null when the session is open or
   *  was closed before the Balancing Summary feature shipped. */
  balancing_date: string | null;
  opened_by_staff_id: string | null;
  closed_by_staff_id: string | null;
  opened_at: string | null;
  closed_at: string | null;
  metrics?: CashDrawerMetrics;
  transactions?: CashTransaction[];
}

export interface CashDrawerOverviewResponse {
  /** Current open session for the salon, or null if none yet. */
  session: CashDrawerSession | null;
  /** Most recent closed sessions (newest first). */
  history: CashDrawerSession[];
}

export interface CashDrawerSingleResponse {
  session: CashDrawerSession;
}

export interface CountDrawerRequest {
  counted_balance?: number;
  counted_denominations?: DenominationEntry[];
  /** Physical total of checks counted at closeout. */
  counted_checks?: number;
  closing_note?: string;
  /** YYYY-MM-DD in the salon's timezone. Persisted on the closing session
   *  to anchor the Balancing Summary's daily sales window. */
  balancing_date?: string;
  /** Override for the new (next) drawer's opening_balance. Defaults to
   *  the counted total when omitted. */
  next_drawer_starting_balance?: number;
}

export interface CountDrawerResponse {
  closed_session: CashDrawerSession;
  new_session: CashDrawerSession;
}

export interface UpdateBalancingDateResponse {
  session: CashDrawerSession;
}

export interface CashDrawerBalancingSummary {
  balancing_date: string | null;
  counted_cash: {
    starting_amount: number;
    ending_amount: number;
    ending_denominations: {
      hundred: number;
      fifty: number;
      twenty: number;
      ten: number;
      five: number;
      two: number;
      one: number;
      coins: number;
    };
    /** Physical total of checks captured at closeout. Null when the drawer
     *  was closed before the Checks feature shipped. */
    counted_checks: number | null;
  };
  daily_sales: {
    cash: number;
    check: number;
    zelle: number;
    venmo: number;
    cashapp: number;
    card: number;
    /** Per-salon custom Off-Platform Payment methods (e.g. "Care Credit",
     *  "Groupon") with non-zero activity in this session's day. Rendered
     *  inline alongside the hardcoded six rows in the Balancing Summary
     *  modal — each entry gets its own "{name} Transactions" line. */
    custom_payments?: Array<{ name: string; amount: number }>;
    service_sales: number;
    net_retail_sales: number;
    tips: number;
    taxes: number;
    total: number;
    cash_over_short: number;
  };
}

export interface CashDrawerBalancingSummaryResponse {
  session: CashDrawerSession;
  summary: CashDrawerBalancingSummary;
}

export type ManualCashTransactionType = "cash_in" | "cash_out";

export interface CreateCashTransactionRequest {
  type: ManualCashTransactionType;
  amount: number;
  description?: string;
  custom_type_label?: string;
  /** Required when type is 'cash_out'. */
  payout_category?: PayoutCategory;
}

export interface CashDrawerReport {
  session: CashDrawerSession;
  metrics: CashDrawerMetrics;
  transactions: CashTransaction[];
}

export interface CashTenderDetails {
  /**
   * Bill portion the cash covers — capped at the balance due. Anything left
   * over (when the customer hands you more than they owe) is split between
   * the tip the customer chose to leave and the change returned to them.
   */
  amount: number;
  tendered: number;
  change: number;
  tip_amount: number;
}
